// The per-zoom animation mode the render body runs in (design D7, UI.md §8): one of
// three modes over the wire's two LOD tiers. `full` is the close tier at nominal load
// (heading-frame swap, hover-bob, offset shadow, ground crossfade, interpolation);
// `reduced` is the close tier under a degradation trigger (heading + interpolation kept,
// hover-bob + shadow + crossfade dropped); `none` is the wide tier (static swarms and
// markers, no per-entity animation).
//
// Two pieces live here, both pure/GPU-free and unit-tested in `animation-mode.test.ts`:
//
//   1. `mode(tier, degraded)` — the pure per-frame selector mapping the binary wire tier
//      + a STABLE degraded boolean onto the three modes.
//   2. `DegradationMonitor` — the small anti-flap state machine that PRODUCES that stable
//      boolean from the raw per-frame `ClosePayload.truncated`/`totalCount` cap pressure
//      (plus an optional frame-rate guard). The wire LOD hysteresis (±10%) governs the
//      tier; this monitor adds a separate measurement window so the degradation trigger —
//      which is per-frame noisy — never flaps the mode.
//
// The season ground crossfade (group 7) and the anchor / RAF-loop draw body (groups 8, 10,
// 11) consume the `AnimationMode` type; group 11 wires the selector + monitor into the draw
// body. Nothing here reads a GPU; the monitor's window is driven by the injected `Clock`
// (group 1.1), so its timing is deterministically testable against a fake clock.

import { Tier } from '../net/wire/messages';
import type { Clock } from './frame-scheduler';

/** One of the three per-zoom animation modes (design D7). */
export type AnimationMode = 'full' | 'reduced' | 'none';

/**
 * The pure per-frame animation-mode selector (design D7). The wide tier is always
 * `none` (static swarms and markers); the close tier is `reduced` under the degradation
 * signal, else `full`. `degraded` is the STABLE boolean the `DegradationMonitor` produces
 * — never a raw per-frame indicator — so the mode inherits the monitor's anti-flap
 * guarantee. Total over its inputs: a new tier or a new mode is a compile error here.
 */
export function mode(tier: Tier, degraded: boolean): AnimationMode {
  if (tier === Tier.Wide) return 'none';
  return degraded ? 'reduced' : 'full';
}

/** Default anti-flap window (ms): the raw degradation indicator must hold its new value
 * continuously for this long before the stable signal flips. At ~10 Hz close-tier deltas
 * this smooths a few noisy frames without a perceptible lag entering/leaving `reduced`. */
export const DEFAULT_DEGRADATION_WINDOW_MS = 500;

/** The raw per-frame cap-pressure inputs from the close payload (design D7): the dangling
 * `ClosePayload.truncated`/`totalCount` fields this monitor finally consumes. */
export interface DegradationSignal {
  /** The close payload was truncated at `close_lod_cap` (population at the cap). */
  readonly truncated: boolean;
  /** The reported total close-tier population (before truncation). */
  readonly totalCount: number;
}

/** The optional frame-rate guard (design D7): a rendered frame slower than `1000/minFps`
 * milliseconds is a raw guard trip. The measurement window + anti-flap hysteresis lives in
 * `DegradationMonitor`, so a single slow frame never degrades — the guard must trip
 * continuously across the window. */
export interface FrameRateGuard {
  /** Below this measured fps a frame counts as a guard trip. */
  readonly minFps: number;
}

export interface DegradationOptions {
  /** Anti-flap window in ms (default `DEFAULT_DEGRADATION_WINDOW_MS`). */
  readonly windowMs?: number;
  /** Population at/above which the cap signal trips even without a hard `truncated`
   * (default `Infinity` — only `truncated` trips the cap signal, matching the spec's
   * "reports truncated" path). Wires `totalCount` into the trigger. */
  readonly capPopulation?: number;
  /** Optional frame-rate guard; omit to disable (cap signal only). */
  readonly frameGuard?: FrameRateGuard;
}

/**
 * The anti-flap degradation state machine (design D7). Fed once per rendered frame with the
 * close payload's cap pressure, it derives a raw per-frame degradation indicator
 * (`truncated` / population cap, or the optional frame-rate guard) and commits it to a
 * STABLE `degraded` boolean only after the raw indicator holds its new value continuously
 * for the measurement window. While the raw indicator oscillates around its threshold —
 * flapping true/false faster than the window — every frame that agrees with the committed
 * state cancels the pending flip, so the mode does not switch every frame; it changes only
 * once the signal clears the anti-flap band (in either direction, symmetric recovery).
 *
 * The window is measured against an injected `Clock` (group 1.1), so the timing is
 * deterministic under a fake clock. Pure of any GPU/Pixi dependency.
 */
export class DegradationMonitor {
  private readonly windowMs: number;
  private readonly capPopulation: number;
  private readonly frameGuard: FrameRateGuard | null;
  /** Frame-time budget (ms) above which the guard trips; `Infinity` when no guard. */
  private readonly slowFrameMs: number;
  private degradedState = false;
  /** When the raw indicator first disagreed with the committed state; `null` while it
   * agrees (no pending flip). */
  private pendingSince: number | null = null;
  /** The previous frame's timestamp, for the inter-frame delta the guard measures. */
  private lastFrameNow: number | null = null;

  constructor(
    private readonly clock: Clock,
    options: DegradationOptions = {},
  ) {
    this.windowMs = options.windowMs ?? DEFAULT_DEGRADATION_WINDOW_MS;
    this.capPopulation = options.capPopulation ?? Number.POSITIVE_INFINITY;
    this.frameGuard = options.frameGuard ?? null;
    this.slowFrameMs = this.frameGuard
      ? 1000 / this.frameGuard.minFps
      : Number.POSITIVE_INFINITY;
  }

  /** The stable degraded signal (anti-flap-committed). */
  get degraded(): boolean {
    return this.degradedState;
  }

  /**
   * Consume one rendered frame's degradation inputs and return the stable degraded signal.
   * The raw per-frame indicator is `truncated || totalCount ≥ capPopulation` OR the
   * frame-rate guard trip (frame slower than the budget); it commits to `degraded` only
   * after holding continuously for the measurement window.
   */
  sample(signal: DegradationSignal): boolean {
    const now = this.clock.now();
    const firstFrame = this.lastFrameNow === null;
    const frameDeltaMs = firstFrame ? 0 : now - (this.lastFrameNow as number);
    this.lastFrameNow = now;

    const capActive =
      signal.truncated || signal.totalCount >= this.capPopulation;
    // The guard cannot trip on the first frame (no prior delta to measure).
    const guardActive =
      this.frameGuard !== null && !firstFrame && frameDeltaMs > this.slowFrameMs;
    const rawActive = capActive || guardActive;

    if (rawActive === this.degradedState) {
      // The raw indicator agrees with the committed state → cancel any pending flip. This
      // is the anti-flap: an oscillating signal keeps clearing the pending timer here.
      this.pendingSince = null;
    } else if (this.pendingSince === null) {
      this.pendingSince = now; // new candidate: start the dwell timer
    } else if (now - this.pendingSince >= this.windowMs) {
      // Held the opposite value for the full window → commit the flip (once).
      this.degradedState = rawActive;
      this.pendingSince = null;
    }
    return this.degradedState;
  }
}
